import type { SalesViewSummary, SalesFilterOptions, SalesSortMode } from '../../api/types';
import { SalesFilterDropdown, type FilterOption } from './SalesFilterDropdown';
import { toggleValue, type SalesFilterState, type SalesFlagFilters } from './salesViewFilters';
import { ENGAGEMENT_COLOR, NEUTRAL_DOT, OPPORTUNITY_DOT, TIER_COLOR } from './salesViewTokens';

const SORT_LABEL: Record<SalesSortMode, string> = {
  health: 'Health',
  contact: 'Needs contact',
  both: 'Both',
  band: 'Workflow band',
};

interface SalesCarouselControlsProps {
  filters: SalesFilterState;
  flags: SalesFlagFilters;
  summary: SalesViewSummary | null;
  options: SalesFilterOptions | null;
  sort: SalesSortMode;
  onFiltersChange: (next: SalesFilterState) => void;
  onFlagsChange: (next: SalesFlagFilters) => void;
  onSortChange: (next: SalesSortMode) => void;
}

/**
 * The carousel toolbar (§6.3a), mirroring Client View's sort / filter row.
 *
 * The narrower filters live HERE rather than in the chip rows above: they qualify
 * the list rather than defining the triage axis, and three more chip rows at the
 * top pushed the carousel below the fold.
 */
export function SalesCarouselControls({
  filters,
  flags,
  summary,
  options,
  sort,
  onFiltersChange,
  onFlagsChange,
  onSortChange,
}: SalesCarouselControlsProps) {
  const reasonCounts = summary?.reason_group_counts ?? {};
  const opportunityCounts = summary?.opportunity_counts ?? {};
  const engagementCounts = summary?.engagement_band_counts ?? {};

  // Server vocabulary, not a frozen list: reason groups gain members at score
  // v1.5 and a literal list here would hide the new ones.
  const reasonOptions: FilterOption[] = (options?.reason_groups ?? []).map((g) => ({
    value: g,
    label: g,
    color: NEUTRAL_DOT,
    count: reasonCounts[g] ?? 0,
  }));

  // Server vocabulary again, and the label has to come with it: the codes are not
  // readable and the per-client labels carry that client's own numbers.
  const opportunityOptions: FilterOption[] = (options?.opportunities ?? []).map((o) => ({
    value: o.code,
    label: o.label,
    color: OPPORTUNITY_DOT,
    count: opportunityCounts[o.code] ?? 0,
  }));

  const engagementOptions: FilterOption[] = (options?.engagement_bands ?? []).map((b) => ({
    value: b,
    label: b,
    color: ENGAGEMENT_COLOR[b] ?? NEUTRAL_DOT,
    count: engagementCounts[b] ?? 0,
  }));

  const flagOptions: FilterOption[] = [
    {
      value: 'wentDark',
      label: 'Went Dark',
      color: TIER_COLOR.URGENT.pin,
      count: summary?.went_dark_count ?? 0,
    },
    {
      value: 'targetMissing',
      label: 'Target Missing',
      color: NEUTRAL_DOT,
      count: summary?.target_missing_count ?? 0,
    },
  ];

  const activeFlags = (Object.keys(flags) as (keyof SalesFlagFilters)[]).filter((k) => flags[k]);

  return (
    <div className="sv-carousel-header">
      <label className="sv-sort" title="What the carousel is ranked by">
        <span className="sv-sort-label">Sort</span>
        <select
          className="sv-sort-select"
          value={sort}
          onChange={(e) => onSortChange(e.target.value as SalesSortMode)}
        >
          {(Object.keys(SORT_LABEL) as SalesSortMode[]).map((s) => (
            <option key={s} value={s}>{SORT_LABEL[s]}</option>
          ))}
        </select>
      </label>

      {/* One flex item, so the three drop to a second line together rather than
          one staying up beside Sort and two falling below it. */}
      <div className="sv-carousel-filters">
        <SalesFilterDropdown
          label="Why surfaced"
          title="The reason this client scored onto the board"
          options={reasonOptions}
          selected={filters.reason_group}
          onToggle={(v) =>
            onFiltersChange({ ...filters, reason_group: toggleValue(filters.reason_group, v) })
          }
          onClear={() => onFiltersChange({ ...filters, reason_group: [] })}
        />
        <SalesFilterDropdown
          label="Opportunity"
          title="Win their business, grow their share, momentum, or a product not sold yet. Never affects the health score; Plan My Week weighs all but whitespace."
          options={opportunityOptions}
          selected={filters.opportunity}
          onToggle={(v) =>
            onFiltersChange({ ...filters, opportunity: toggleValue(filters.opportunity, v) })
          }
          onClear={() => onFiltersChange({ ...filters, opportunity: [] })}
        />
        <SalesFilterDropdown
          label="Engagement"
          title="Days since the last CSE-logged contact"
          options={engagementOptions}
          selected={filters.engagement_band}
          onToggle={(v) =>
            onFiltersChange({ ...filters, engagement_band: toggleValue(filters.engagement_band, v) })
          }
          onClear={() => onFiltersChange({ ...filters, engagement_band: [] })}
        />
        <SalesFilterDropdown
          label="Flags"
          options={flagOptions}
          selected={activeFlags}
          onToggle={(v) => {
            const key = v as keyof SalesFlagFilters;
            onFlagsChange({ ...flags, [key]: !flags[key] });
          }}
          onClear={() => onFlagsChange({ wentDark: false, targetMissing: false })}
        />
      </div>
    </div>
  );
}

export default SalesCarouselControls;
